/**
 * Contractor entry-window rules (0.25.0). A contractor never schedules entry — they PROPOSE a
 * window and the OWNER confirms it (which serves the §1954 notice). A proposal must satisfy:
 *
 *   - start ≥ the `entry_notice_hours` rule (24h) in the future at proposal time (re-checked when
 *     the owner confirms — 409 `proposal_too_late`);
 *   - an explicit END after the start;
 *   - at most `maxWindowHours` long;
 *   - inside normal business hours: `weekdays` (ISO 1=Mon…7=Sun) between `startMinute` and
 *     `endMinute` (minutes after local midnight) in `timeZone`, on ONE local day;
 *   - at most `maxProposalsPer24h` proposals per job in any rolling 24h (429).
 *
 * These are constants (not `rule_tables`) because they are an operator policy for vendors, not a
 * statute; adjust them here. Pure + shared so the API enforces and the SPA can explain the same rule.
 */
export const CONTRACTOR_ENTRY_RULES = {
  timeZone: 'America/Los_Angeles',
  weekdays: [1, 2, 3, 4, 5] as readonly number[],
  startMinute: 8 * 60,
  endMinute: 18 * 60,
  maxWindowHours: 8,
  maxProposalsPer24h: 2,
} as const;

export type ContractorEntryRules = typeof CONTRACTOR_ENTRY_RULES;

export type ContractorWindowError =
  | 'entry_end_required'
  | 'entry_end_before_start'
  | 'entry_window_too_long'
  | 'entry_outside_business_hours';

const WEEKDAY: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

/** Local calendar date, ISO weekday, and minutes-after-midnight of an instant in `timeZone`. */
function localParts(iso: string, timeZone: string): { date: string; weekday: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    weekday: WEEKDAY[get('weekday')] ?? 0,
    minute: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

/** Validate a contractor entry window; returns null when allowed, else the error code. */
export function checkContractorEntryWindow(
  startIso: string,
  endIso: string | null | undefined,
  rules: ContractorEntryRules = CONTRACTOR_ENTRY_RULES,
): ContractorWindowError | null {
  if (!endIso) return 'entry_end_required';
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!(end > start)) return 'entry_end_before_start';
  if (end - start > rules.maxWindowHours * 3_600_000) return 'entry_window_too_long';
  const s = localParts(startIso, rules.timeZone);
  const e = localParts(endIso, rules.timeZone);
  if (s.date !== e.date) return 'entry_outside_business_hours';
  if (!rules.weekdays.includes(s.weekday)) return 'entry_outside_business_hours';
  if (s.minute < rules.startMinute || e.minute > rules.endMinute) return 'entry_outside_business_hours';
  return null;
}
